import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Smartphone, Apple, Play, X, Bell, Check } from "lucide-react";
import { getMobileRedirectTarget, notifyMobileLaunch } from "@/lib/api";

const CORE_LOGIN_URL = process.env.REACT_APP_PROGERE_CORE_LOGIN_URL || "/login";
const REDIRECT_DELAY_MS = Number(process.env.REACT_APP_PROGERE_MOBILE_REDIRECT_DELAY_MS || 3000);

// Bouton « PROGÈRE ARRIVE BIENTÔT » : à l'ouverture, affiche une fenêtre
// d'information + décompte, puis redirige l'utilisateur connecté vers SON
// PROGÈRE CORE. La destination est déterminée CÔTÉ SERVEUR à partir de la
// session (aucun organization_id/company_id/permission dans l'URL). La session,
// le thème et la langue sont conservés. Inclut un mini formulaire « Prévenez-moi
// au lancement » (capture d'email) qui met le décompte en pause.
export function ProgereDownloadAppButton({
  variant = "card", size = "md", moduleCode = "core",
  showSubtitle = true, className = "",
}) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [countdown, setCountdown] = useState(Math.round(REDIRECT_DELAY_MS / 1000));
  const [paused, setPaused] = useState(false);
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [signedUp, setSignedUp] = useState(false);
  const targetRef = useRef({ authenticated: true, dashboard_path: "/app", login_url: CORE_LOGIN_URL });
  const timersRef = useRef([]);
  const sourceTag = variant === "card" ? "sidebar" : "web";

  const clearTimers = () => {
    timersRef.current.forEach((t) => { clearInterval(t); clearTimeout(t); });
    timersRef.current = [];
  };

  const doRedirect = () => {
    const t = targetRef.current;
    if (t.authenticated) navigate(t.dashboard_path || "/app");
    else window.location.href = t.login_url || CORE_LOGIN_URL;
  };

  const pauseCountdown = () => {
    if (paused) return;
    clearTimers();
    setPaused(true);
  };

  const start = async () => {
    setCountdown(Math.max(1, Math.round(REDIRECT_DELAY_MS / 1000)));
    setPaused(false);
    setSignedUp(false);
    setOpen(true);
    try {
      const data = await getMobileRedirectTarget(sourceTag);
      targetRef.current = {
        authenticated: data.authenticated !== false,
        dashboard_path: data.dashboard_path || "/app",
        login_url: data.login_url || CORE_LOGIN_URL,
      };
    } catch (e) {
      targetRef.current = { authenticated: false, login_url: CORE_LOGIN_URL };
    }
    const iv = setInterval(() => setCountdown((c) => (c > 1 ? c - 1 : 0)), 1000);
    timersRef.current.push(iv);
    const to = setTimeout(() => { clearTimers(); setOpen(false); doRedirect(); }, REDIRECT_DELAY_MS);
    timersRef.current.push(to);
  };

  const cancel = () => { clearTimers(); setOpen(false); };

  const submitSignup = async (e) => {
    e.preventDefault();
    const value = email.trim();
    if (!value || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      toast.error("Entrez une adresse courriel valide.");
      return;
    }
    setSubmitting(true);
    try {
      const lang = (localStorage.getItem("pg_lang") || "fr").slice(0, 2);
      const res = await notifyMobileLaunch(value, lang, sourceTag);
      setSignedUp(true);
      toast.success(res?.message || "Merci ! Vous serez averti au lancement.");
    } catch (err) {
      toast.error("Impossible d'enregistrer votre courriel. Réessayez.");
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => () => clearTimers(), []);

  const cls = ["progere-download-app-button", `pg-dl--${variant}`, `pg-dl--${size}`, className].filter(Boolean).join(" ");

  return (
    <>
      <button
        type="button"
        className={cls}
        onClick={start}
        data-testid="download-app-button"
        aria-label="PROGÈRE arrive bientôt"
      >
        <span className="pg-dl__icon"><Smartphone size={18} aria-hidden="true" /></span>
        <span className="pg-dl__content">
          <span className="pg-dl__title">PROGÈRE ARRIVE BIENTÔT</span>
          {showSubtitle && (
            <span className="pg-dl__subtitle">L'application mobile sera bientôt disponible sur Apple et Android.</span>
          )}
        </span>
      </button>

      {open && (
        <div className="pg-dl-modal__backdrop" role="presentation" data-testid="mobile-comingsoon-modal">
          <section className="pg-dl-modal" role="dialog" aria-modal="true">
            <button type="button" className="pg-dl-modal__close" onClick={cancel} aria-label="Fermer"><X size={18} /></button>
            <div className="pg-dl-modal__brand">
              <span className="pg-pro">PRO</span>
              <span className="pg-gere">GÈRE</span>
              <span className="progere-brand-module">&nbsp;MOBILE</span>
            </div>
            <h2 className="pg-dl-modal__title" data-testid="mobile-comingsoon-title">PROGÈRE ARRIVE BIENTÔT</h2>
            <p className="pg-dl-modal__text">
              L'application mobile PROGÈRE sera bientôt disponible sur Apple et Android.
              Vous serez redirigé vers votre PROGÈRE CORE.
            </p>
            <div className="pg-dl-modal__stores" aria-hidden="true">
              <span className="pg-store-btn pg-store-btn--soon"><Apple size={24} /><span><small>Bientôt sur</small><strong>l'App Store</strong></span></span>
              <span className="pg-store-btn pg-store-btn--soon"><Play size={22} /><span><small>Bientôt sur</small><strong>Google Play</strong></span></span>
            </div>

            {signedUp ? (
              <div className="pg-dl-modal__signup pg-dl-modal__signup--done" data-testid="mobile-launch-done">
                <Check size={16} /> Merci ! Vous serez averti dès le lancement.
              </div>
            ) : (
              <form className="pg-dl-modal__signup" onSubmit={submitSignup} data-testid="mobile-launch-form">
                <label className="pg-dl-modal__signup-label"><Bell size={13} /> Prévenez-moi au lancement</label>
                <div className="pg-dl-modal__signup-row">
                  <input
                    type="email"
                    className="pg-dl-modal__signup-input"
                    placeholder="votre@courriel.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onFocus={pauseCountdown}
                    data-testid="mobile-launch-email"
                    autoComplete="email"
                  />
                  <button type="submit" className="pg-primary-btn" disabled={submitting} data-testid="mobile-launch-submit">
                    {submitting ? "…" : "M'avertir"}
                  </button>
                </div>
              </form>
            )}

            {!paused ? (
              <p className="pg-dl-modal__soon" data-testid="mobile-comingsoon-countdown">
                Redirection dans {countdown}s…
              </p>
            ) : (
              <p className="pg-dl-modal__soon" data-testid="mobile-comingsoon-paused">
                Redirection en pause. Utilisez le bouton ci-dessous quand vous êtes prêt.
              </p>
            )}

            <div className="pg-dl-modal__actions">
              <button type="button" className="pg-secondary-btn" onClick={cancel}>Annuler</button>
              <button type="button" className="pg-primary-btn" onClick={() => { clearTimers(); setOpen(false); doRedirect(); }} data-testid="mobile-comingsoon-now">
                Aller au CORE maintenant
              </button>
            </div>
          </section>
        </div>
      )}
    </>
  );
}
